import { useSocketSubscription } from 'shared/hooks/pubsub';
import { PubsubChannelType, SalesfloorParticipant } from 'sb-lib/types';
import { useEffect, useRef, useMemo } from 'react';
import {
  selectAllRoomsWithLiveListen,
  setLocalParticipantStatus,
  updateSalesfloorRoom,
} from 'shared/store/salesfloor-slice';
import { leaveSalesfloorRoom, updateSalesfloorRoomParticipant } from 'shared/api';
import { useAppDispatch, useAppSelector } from 'shared/store';
import {
  SalesfloorRoom as SalesfloorRoomType,
  SalesfloorRoomParticipantStatus,
} from 'types/salesfloor';
import { VideoRoomManager } from 'shared/hooks/video-room';
import { useBeforeUnload } from 'shared/hooks/before-unload';
import { useIsPageHidden, usePrevious } from 'shared/hooks/misc';
import { useOutboundContext } from 'browser/components/Outbound/outbound-provider';
import { makeLog } from 'shared/utils/log';
import { OutboundSessionStates } from 'browser/components/Outbound/OutboundSession';
import { selectActiveUser } from 'shared/store/auth-slice';
import { encodeParticipantId } from 'shared/utils/salesfloor';
import { selectActivePublicUser } from 'shared/store/shared-selectors';
import { debounce } from 'lodash';

const log = makeLog('SalesfloorRoomManager');

export function useSalesfloorRoom(
  salesfloorRoomId: string | null,
  videoRoomManager: VideoRoomManager,
): SalesfloorRoomType | null {
  const dispatch = useAppDispatch();
  const activeUser = useAppSelector(selectActiveUser);
  const activePublicUser = useAppSelector(selectActivePublicUser);
  const isRoomConnected = videoRoomManager.videoRoom?.isConnected || false;
  const prevIsRoomConnected = usePrevious(isRoomConnected);
  const { localParticipantStatus } = useAppSelector(s => s.salesfloor);
  const prevStatus = usePrevious(localParticipantStatus);
  const isPageHidden = useIsPageHidden();
  const closedTab = useRef<boolean>(false);
  const { session: sessionRef, pristineMp3 } = useOutboundContext();
  const outboundStatus = useAppSelector(s => s.outbound.status);
  const currentOutboundCall = sessionRef?.current?.twilioInstance?.call || null;
  const { setLocalExtraAudioTrack } = videoRoomManager;
  const allRooms = useAppSelector(selectAllRoomsWithLiveListen);
  const salesfloorRoom = allRooms.find(r => r.id === salesfloorRoomId) || null;
  const otherParticipants = salesfloorRoom?.participants.filter(p => {
    if (activeUser) {
      return p.id !== encodeParticipantId(activeUser.id);
    }

    return p.id !== encodeParticipantId(activePublicUser?.id, true);
  });
  const prevOtherParticipants = usePrevious(otherParticipants);
  const lastStatusRef = useRef<SalesfloorRoomParticipantStatus | null>(null);
  const lastStatusUpdateTimestampRef = useRef<number>(0);

  useSocketSubscription(
    PubsubChannelType['salesfloor.room'],
    salesfloorRoomId || undefined,
  );

  useEffect(() => {
    async function leaveRoom() {
      if (!salesfloorRoomId) {
        log.info('Not leaving room. salesfloorRoomId is null.');
        return;
      }
      dispatch(updateSalesfloorRoom((await leaveSalesfloorRoom(salesfloorRoomId)).data));
      dispatch(setLocalParticipantStatus(null));
    }

    return () => {
      log.debug(
        `Leaving room ${salesfloorRoomId}. Room id changed or component unmounted.`,
      );
      void leaveRoom();
    };
  }, [dispatch, salesfloorRoomId]);

  useEffect(() => {
    otherParticipants?.forEach(p => {
      if (
        p.status === 'connected' &&
        prevOtherParticipants?.find(x => x.id === p.id)?.status !== 'connected'
      ) {
        log.info(`Participant ${p.id} connected. Playing audio.`);
        pristineMp3.current.volume = 0.4;
        void pristineMp3.current.play();
      }
    });
  }, [otherParticipants, prevOtherParticipants, pristineMp3]);

  const debouncedSubmitStatus = useMemo(
    () =>
      debounce(async () => {
        if (!salesfloorRoomId || closedTab.current) {
          log.info('Not submitting status. salesfloorRoomId is null or tab is closed.');
          return;
        }
        let newRoomResponse: { data: SalesfloorRoomType } | null = null;
        if (isRoomConnected) {
          let status: SalesfloorRoomParticipantStatus = isPageHidden
            ? 'inactive'
            : 'active';

          switch (outboundStatus) {
            case OutboundSessionStates.connected:
              status = 'connected';
              break;
            case OutboundSessionStates.reconnecting:
              status = prevStatus ?? status;
              break;
            case OutboundSessionStates.acceptingCall:
            case OutboundSessionStates.awaitingUpdates:
            case OutboundSessionStates.dialing:
              status = 'dialing';
              break;
          }

          if (status !== lastStatusRef.current) {
            lastStatusRef.current = status;
            lastStatusUpdateTimestampRef.current = Date.now();
            newRoomResponse = await updateSalesfloorRoomParticipant(salesfloorRoomId, {
              status: status,
            });
            dispatch(setLocalParticipantStatus(status));
            log.info(`Changed status from ${prevStatus} to ${status}`);

            if (status === 'connected' && prevStatus !== 'connected') {
              const outboundAudio = currentOutboundCall
                ?.getRemoteStream()
                ?.getAudioTracks()[0];
              if (!outboundAudio) {
                log.info('No outbound audio track found. Not mixing audio.');
                return;
              }
              await setLocalExtraAudioTrack(outboundAudio);
            }

            if (prevStatus === 'connected' && status !== 'connected') {
              await setLocalExtraAudioTrack(null);
            }
          }
        } else if (prevIsRoomConnected) {
          log.info(`Changed status from ${prevStatus} to disconnected`);
          newRoomResponse = await leaveSalesfloorRoom(salesfloorRoomId);
          dispatch(setLocalParticipantStatus(null));
        }

        if (newRoomResponse) {
          dispatch(updateSalesfloorRoom(newRoomResponse.data));
        }
      }, 300),
    [
      salesfloorRoomId,
      isRoomConnected,
      prevIsRoomConnected,
      isPageHidden,
      outboundStatus,
      prevStatus,
      dispatch,
      currentOutboundCall,
      setLocalExtraAudioTrack,
    ],
  );

  useEffect(() => {
    debouncedSubmitStatus();

    if (isRoomConnected) {
      const interval = setInterval(() => {
        debouncedSubmitStatus();
      }, 10000);
      return () => {
        clearInterval(interval);
        debouncedSubmitStatus.cancel();
      };
    }

    return () => {
      debouncedSubmitStatus.cancel();
    };
  }, [debouncedSubmitStatus, isRoomConnected]);

  useBeforeUnload(() => {
    log.info('Tab closed.');
    closedTab.current = true;
    if (salesfloorRoomId && (activePublicUser || activeUser)) {
      log.info('Leaving salesfloor room.');
      void leaveSalesfloorRoom(salesfloorRoomId);
    }
  });

  return salesfloorRoom;
}

export function useSalesfloorParticipant(): SalesfloorParticipant | null {
  const user = useAppSelector(selectActiveUser);
  const publicUser = useAppSelector(selectActivePublicUser);
  const agent = user ? user : publicUser;
  return useMemo(() => {
    if (!agent) {
      log.warn('Missing agent or publicUser details.');
      return null;
    }
    if (user) {
      return {
        id: `user_${agent.id}`,
        displayName: agent.displayName,
        title: agent.title,
        avatarUrl: agent.avatar ? agent.avatar?.imageUrl : null,
        lastActivity: '',
        status: 'active',
      };
    } else {
      return {
        id: `public_user_${agent.id}`,
        displayName: agent.displayName,
        title: agent.title,
        avatarUrl: agent.avatar ? agent.avatar?.imageUrl : null,
        lastActivity: '',
        status: 'active',
      };
    }
  }, [agent, user]);
}
